'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Modal from 'react-modal';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel, Thumbs, Keyboard, FreeMode, Virtual } from 'swiper/modules';
import { mediaQueries } from '../../common/styleguide';
import { IoCloseOutline } from 'react-icons/io5';
import { IoChevronForward, IoChevronBack } from 'react-icons/io5';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import 'swiper/css/mousewheel';
import 'swiper/css/free-mode';
import 'swiper/css/thumbs';
import { destroyAdsGallery, requestAdsGallery } from '../ads/AdHandlerService';
import * as stylex from '@stylexjs/stylex';
import '@/common/tokens.stylex';
import { colors } from '@/common/tokens.stylex';
import { stylexProps } from '@/util/stylexProps';
type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  initialSlide: number;
  images: [];
  title: string;
  id: string;
};
function GalleryModal(props: Props) {
  const {
    images,
    isOpen,
    onRequestClose,
    initialSlide,
    title,
    id
  } = props;
  const [isMobile, setIsMobile] = useState(true);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const router = useRouter();
  useEffect(() => {
    const hashChangeListener = () => {
      if (window.location.hash !== '#galerie') {
        onRequestClose();
        requestAdsGallery();
      }
    };
    window.addEventListener('hashchange', hashChangeListener);
    return () => {
      window.removeEventListener('hashchange', hashChangeListener);
    };
  }, [onRequestClose]);
  useEffect(() => {
    if (isOpen) {
      window.location.hash = '#galerie';
      window.dataLayer?.push({
        event: 'custom_event',
        event_name: 'gallery_fullscreen',
        gallery_fullscreen: {
          gallery_title: title,
          gallery_id: id
        }
      });
      destroyAdsGallery();
    }
  }, [id, isOpen, title]);
  useEffect(() => {
    const mql = window.matchMedia(`(${mediaQueries.minWidthDesktop})`);
    const isMobile = !mql.matches;
    setIsMobile(isMobile);
  }, []);
  const slideChanged = useCallback(swiper => {
    window.dataLayer?.push({
      event: 'custom_event',
      event_name: 'gallery_swipe',
      gallery_swipe: {
        gallery_title: title,
        gallery_id: id,
        total_slides: swiper.slidesGrid.length + '',
        slide_number: swiper.activeIndex + ''
      }
    });
  }, [id, title]);
  return <Modal isOpen={isOpen} contentLabel="Bilder-Gallerie" className={{
    className: "xixxii4 x13vifvy xu96u03 x3m8u43 x1ey2m1c x14a4a2e x1pgnn3v xb3r6kr"
  }.className} overlayClassName={{
    className: "x14a4a2e"
  }.className} onRequestClose={() => {
    router.back();
    requestAdsGallery();
  }} shouldCloseOnEsc>
      <div className="x1pgnn3v x1jafw1f x1n2onr6 xh8yej3 x5yr21d">
        <Swiper modules={[Navigation, FreeMode, Mousewheel, Thumbs, Keyboard, Virtual]} thumbs={{
        swiper: thumbsSwiper
      }} virtual mousewheel keyboard grabCursor spaceBetween={10} onSlideChange={slideChanged} navigation={{
        nextEl: '.next',
        prevEl: '.prev'
      }} initialSlide={initialSlide} {...stylexProps('swiper-container gallery-top', styles.galleryTop)}>
          {images && images.map((image, i) => <SwiperSlide key={`gallerie-${image.gallery1024.url}`}>
                <div className="xh8yej3 x5yr21d x1n2onr6">
                  <img className="x5yr21d xh8yej3 x19kjcj4 x1lliihq" width={image.gallery1024.width} height={image.gallery1024.height} src={image.gallery1024.url} alt={image.caption} loading="lazy" decoding="async" />
                  <div className="x10l6tqk x1ey2m1c xh8yej3 x1yt3wzz xdmkov xvn2z4z xc7ga6q">
                    <p className="x2b8uid x1qypnhb">{image.caption}</p>
                    <span {...stylexProps('attribution', styles.attribution)}>
                      {image.attribution}
                    </span>
                  </div>
                </div>
              </SwiperSlide>)}
        </Swiper>
        <button title="Gehe nach links" {...stylexProps('prev', styles.nav, styles.prev)}>
          <IoChevronBack className="xxmjlet xvy4d1p xxk0z11 x1ldx8e" />
        </button>
        <button title="Gehe nach rechts" {...stylexProps('next', styles.nav, styles.next)}>
          <IoChevronForward className="xxmjlet xvy4d1p xxk0z11 x1swdo50" />
        </button>
        {!isMobile && <Swiper modules={[Navigation, Mousewheel, Thumbs, FreeMode]} onSwiper={setThumbsSwiper} spaceBetween={10} touchRatio={0.2} slidesPerView={8} freeMode mousewheel watchSlidesProgress centeredSlides initialSlide={initialSlide} {...stylexProps('swiper-container gallery-thumbs', styles.galleryThumbs)}>
            {images && images.map((image, i) => <SwiperSlide key={`thumb-${image.video480?.url ?? image.gallery1024.url}`} {...stylexProps('swiper-slide', styles.thumbSlide)}>
                  <img className="x5yr21d xh8yej3 x19kjcj4" src={image.video480?.url ?? image.gallery1024.url} alt={image.caption} loading="lazy" decoding="async" />
                </SwiperSlide>)}
          </Swiper>}
      </div>
      <div className="x19up5dg xfr5jun x10l6tqk x1hi9sau x78zum5 xl56j7k x6s0dn4 x1ypdohk xn3w4p2 x187nhsf x1pgnn3v x1jafw1f x10hpsqq" onClick={() => {
      router.back();
      window.dataLayer?.push({
        event: 'custom_event',
        event_name: 'gallery_close',
        gallery_close: {
          gallery_title: title,
          gallery_id: id
        }
      });
      requestAdsGallery();
    }}>
        <IoCloseOutline title="Gallerie Schließen" className="x6bodad xmwxtux x13apeso xgd8bvy x1fgtraw x190ymzz" />
      </div>
    </Modal>;
}
export default GalleryModal;
const styles = {
  galleryTop: {
    kZKoxP: "x5yr21d x1jdbwf9",
    kzqmXN: "xh8yej3",
    kVAEAm: "x1n2onr6",
    $$css: true
  },
  attribution: {
    kMwMTN: "x1ytf6ek x1pdwq98",
    kzqmXN: "xh8yej3",
    k9WMMc: "x2b8uid",
    kGuDYH: "x1k6wstc",
    kLWn49: "x1xxsxie",
    $$css: true
  },
  nav: {
    kVAEAm: "x10l6tqk",
    k87sOh: "xwa60dl",
    kY2c9j: "x1hi9sau",
    k3aq6I: "x1cb1t30",
    kkrTdU: "x1ypdohk",
    k1xSpc: "x78zum5",
    kjj79g: "xl56j7k",
    kGNEyG: "x6s0dn4",
    kaIpWk: "x10hpsqq",
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    k1ekBW: "x19991ni",
    kWkggS: "x3r82pw xhs0opo x1cgtfzf",
    $$css: true
  },
  prev: {
    kbCHJM: "xncym2f xjaxilg",
    $$css: true
  },
  next: {
    kCIrl2: "x19up5dg x12lfq1u",
    $$css: true
  },
  galleryThumbs: {
    kWkggS: "x694uxj x1eneg08",
    k8WAf4: "xqp6ajr",
    kg3NbH: "xnjsko4",
    kzqmXN: "xh8yej3",
    kZKoxP: "xoverlg",
    koQZXg: "x8x9d4c",
    km5ZXQ: "xack27t",
    $$css: true
  },
  thumbSlide: {
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kSiTet: "xti2d7y",
    kkrTdU: "x1ypdohk",
    ku685b: "x1funxwb",
    $$css: true
  }
};